// IMEI extraction and device lookup service for smart device insurance
import { validateDocument, VISTA_CASE_TYPES, VISTA_DOCUMENT_TYPES } from './vistaOcrService';
import {
  storeSmartDeviceFile,
  SMART_DEVICE_FILE_KEYS,
} from '../utils/smartDeviceFilesStorage';

/**
 * IMEI service for extracting IMEI from screenshots and looking up device information
 * Uses Vista OCR service for IMEI extraction and IMEI.info API for device lookup
 */

// IMEI API configuration with multiple fallback options
const getIMEIAPIConfig = () => {
  // Try multiple sources for configuration
  const config = {
    baseUrl: process.env.REACT_APP_IMEI_API_BASE_URL || 'https://dash.imei.info/api/check',
    serviceId: process.env.REACT_APP_IMEI_API_SERVICE_ID || '0',
    apiKey: process.env.REACT_APP_IMEI_API_KEY,
  };

  // Additional fallback: check if there's a runtime config in localStorage (for debugging)
  if (!config.apiKey && typeof window !== 'undefined') {
    const runtimeApiKey = localStorage.getItem('IMEI_API_KEY_DEBUG');
    if (runtimeApiKey) {
      config.apiKey = runtimeApiKey;
      console.log('🔧 Using runtime API key from localStorage');
    }
  }

  return config;
};

const IMEI_API_CONFIG = getIMEIAPIConfig();

// Debug logging for configuration
console.log('🔧 Environment Variables Debug:', {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_IMEI_API_BASE_URL: process.env.REACT_APP_IMEI_API_BASE_URL,
  REACT_APP_IMEI_API_SERVICE_ID: process.env.REACT_APP_IMEI_API_SERVICE_ID,
  REACT_APP_IMEI_API_KEY_SET: !!process.env.REACT_APP_IMEI_API_KEY,
  REACT_APP_IMEI_API_KEY_LENGTH: process.env.REACT_APP_IMEI_API_KEY ? process.env.REACT_APP_IMEI_API_KEY.length : 0,
  REACT_APP_IMEI_API_KEY_FIRST_CHARS: process.env.REACT_APP_IMEI_API_KEY ? process.env.REACT_APP_IMEI_API_KEY.substring(0, 8) + '...' : 'undefined'
});

console.log('🔧 IMEI API Configuration:', {
  baseUrl: IMEI_API_CONFIG.baseUrl,
  serviceId: IMEI_API_CONFIG.serviceId,
  apiKeyConfigured: !!IMEI_API_CONFIG.apiKey,
  apiKeyLength: IMEI_API_CONFIG.apiKey ? IMEI_API_CONFIG.apiKey.length : 0,
  apiKeyPreview: IMEI_API_CONFIG.apiKey ? IMEI_API_CONFIG.apiKey.substring(0, 8) + '...' : 'undefined'
});

// Add global helper for console access (development only)
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  window.setIMEIKey = (key) => {
    setIMEIAPIKeyRuntime(key || '99bf6e24-3203-4f75-867e-d0664e809a12');
    console.log('🔧 API key set! Refresh the page to use it.');
  };
  window.testIMEI = () => debugIMEIAPI('123456789012345');
  window.showIMEICache = () => {
    const cachedItems = Object.keys(localStorage)
      .filter(key => key.startsWith(IMEI_CACHE_PREFIX))
      .map(key => {
        const data = JSON.parse(localStorage.getItem(key));
        const imei = key.replace(IMEI_CACHE_PREFIX, '');
        const ageHours = Math.round((Date.now() - data.timestamp) / (1000 * 60 * 60));
        return {
          imei,
          brand: data.data.brand,
          model: data.data.model,
          cached: `${ageHours}h ago`,
          expiresIn: `${(24 * 7) - ageHours}h`
        };
      });
    console.table(cachedItems);
    return cachedItems;
  };
  window.clearIMEICache = () => {
    imeiCache.clear();
    console.log('🗑️ IMEI cache cleared');
  };
  console.log('🔧 Global helpers available: setIMEIKey(), testIMEI(), showIMEICache(), clearIMEICache()');
}

// IMEI Vista configuration (replaced DOKI)
// Using Vista OCR with validate-only mode for IMEI extraction

// IMEI cache key prefix
const IMEI_CACHE_PREFIX = 'imei_device_info_';
const CACHE_EXPIRY_HOURS = 24 * 7; // 7 days

/**
 * Cache management for IMEI device information
 */
const imeiCache = {
  /**
   * Get cached device info for IMEI
   * @param {string} imei - IMEI number
   * @returns {Object|null} Cached device info or null if not found/expired
   */
  get: (imei) => {
    try {
      const cacheKey = IMEI_CACHE_PREFIX + imei;
      const cachedData = localStorage.getItem(cacheKey);
      
      if (!cachedData) return null;
      
      const parsed = JSON.parse(cachedData);
      const now = Date.now();
      const expiryTime = parsed.timestamp + (CACHE_EXPIRY_HOURS * 60 * 60 * 1000);
      
      if (now > expiryTime) {
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      return parsed.data;
    } catch (error) {
      console.error('Error reading IMEI cache:', error);
      return null;
    }
  },

  /**
   * Set cached device info for IMEI
   * @param {string} imei - IMEI number
   * @param {Object} deviceInfo - Device information to cache
   */
  set: (imei, deviceInfo) => {
    try {
      const cacheKey = IMEI_CACHE_PREFIX + imei;
      const cacheData = {
        data: deviceInfo,
        timestamp: Date.now(),
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error setting IMEI cache:', error);
    }
  },

  /**
   * Clear all IMEI cache
   */
  clear: () => {
    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith(IMEI_CACHE_PREFIX))
        .forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Error clearing IMEI cache:', error);
    }
  },
};

/**
 * Look up device information using IMEI number
 * @param {string} imei - IMEI number to look up
 * @returns {Promise<Object>} Device lookup result with brand and model
 */
export const lookupDeviceByIMEI = async (imei) => {
  console.log('🔍 Starting IMEI lookup for:', imei);
  
  try {
    // Validate IMEI first
    if (!validateIMEI(imei)) {
      console.log('❌ IMEI validation failed for:', imei);
      return {
        success: false,
        error: {
          type: 'INVALID_IMEI',
          message: 'Invalid IMEI format',
          details: 'IMEI must be exactly 15 digits',
        },
      };
    }

    console.log('✅ IMEI validation passed for:', imei);

    // Check cache first
    const cachedInfo = imeiCache.get(imei);
    if (cachedInfo) {
      console.log('📦 IMEI device info retrieved from cache:', cachedInfo);
      return {
        success: true,
        data: cachedInfo,
        fromCache: true,
      };
    }

    console.log('🔍 No cache found, checking API configuration...');

    // Get fresh config each time to pick up runtime changes
    const currentConfig = getIMEIAPIConfig();
    
    // Check if API key is configured
    if (!currentConfig.apiKey || currentConfig.apiKey === 'your_imei_api_key_here') {
      console.log('❌ IMEI API key not configured');
      return {
        success: false,
        error: {
          type: 'API_NOT_CONFIGURED',
          message: 'IMEI API key not configured',
          details: 'Please configure REACT_APP_IMEI_API_KEY in environment variables or use setIMEIAPIKeyRuntime() for debugging',
        },
      };
    }

    console.log('✅ API key configured, making API call...');

    // Make API call
    const url = `${currentConfig.baseUrl}/${currentConfig.serviceId}/?API_KEY=${currentConfig.apiKey}&imei=${imei}`;
    
    console.log('🌐 Making IMEI API request to:', url.replace(currentConfig.apiKey, '***API_KEY***'));
    console.log('📡 Network request initiated at:', new Date().toISOString());
    
    // Add request interceptor for debugging
    const startTime = Date.now();
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    const endTime = Date.now();
    console.log('🌐 API response received in:', endTime - startTime, 'ms');
    console.log('🌐 API response status:', response.status);
    console.log('🌐 API response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.log('❌ API request failed:', response.status, errorText);
      throw new Error(`API request failed with status: ${response.status} - ${errorText}`);
    }

    const apiData = await response.json();
    console.log('📊 IMEI API response data:', apiData);

    // Check if the API call was successful
    if (apiData.status !== 'Done' || !apiData.result) {
      return {
        success: false,
        error: {
          type: 'API_ERROR',
          message: 'Failed to get device information',
          details: apiData.status || 'Unknown API error',
        },
      };
    }

    // Extract device information
    const deviceInfo = {
      imei: apiData.imei || imei,
      brand: apiData.result.brand_name || '',
      model: apiData.result.model || '',
      lookupTimestamp: Date.now(),
      cost: apiData.token_request_price || '0',
      apiResponse: apiData,
    };

    // Cache the result
    imeiCache.set(imei, deviceInfo);

    return {
      success: true,
      data: deviceInfo,
      fromCache: false,
    };
  } catch (error) {
    console.error('IMEI lookup error:', error);
    return {
      success: false,
      error: {
        type: 'LOOKUP_ERROR',
        message: 'Failed to lookup device information',
        details: error.message,
      },
    };
  }
};

/**
 * Process IMEI screenshot using Vista OCR
 * Uses validate-only mode to extract IMEI without storing in dashboard
 * File is stored for later submission when policy is completed
 * @param {File} file - The IMEI screenshot file to process (JPG, PNG)
 * @returns {Promise<Object>} IMEI extraction result with IMEI number
 */
export const processIMEIScreenshot = async (file) => {
  try {
    // Use Vista OCR with validate-only mode
    const result = await validateDocument(
      file,
      VISTA_DOCUMENT_TYPES.SMART_DEVICE.IMEI,
      VISTA_CASE_TYPES.SMART_DEVICE
    );

    if (!result.success) {
      return result;
    }

    // Store file for later Vista submission
    storeSmartDeviceFile(SMART_DEVICE_FILE_KEYS.IMEI, file);

    // Extract IMEI data from the response
    const output = result.data?.output;

    if (!output) {
      return {
        success: false,
        error: {
          type: 'INVALID_RESPONSE',
          message: 'Invalid IMEI response format',
          details: 'No output data received',
        },
      };
    }

    // Extract IMEI number from Vista response
    // Vista returns: { output: { status: true, data: { IMEI: "..." } } }
    const imeiNumber = output.data?.IMEI || output.data?.imei || output.IMEI || output.imei;

    console.log('[Vista] IMEI extraction result:', {
      imeiNumber,
      rawOutput: output,
    });

    if (!imeiNumber) {
      return {
        success: false,
        error: {
          type: 'IMEI_NOT_FOUND',
          message: 'Could not extract IMEI number from screenshot',
          details: 'Please ensure the screenshot clearly shows the IMEI number',
        },
      };
    }

    return {
      success: true,
      data: {
        imei: imeiNumber,
        extractionTimestamp: Date.now(),
        rawData: output,
      },
      rawResponse: result.rawResponse,
    };
  } catch (error) {
    console.error('[Vista] IMEI processing error:', error);
    return {
      success: false,
      error: {
        type: 'PROCESSING_ERROR',
        message: 'Failed to process IMEI screenshot',
        details: error.message,
      },
    };
  }
};

/**
 * Validate IMEI number format
 * @param {string} imei - IMEI number to validate
 * @returns {boolean} Whether the IMEI is valid format
 */
export const validateIMEI = (imei) => {
  if (!imei || typeof imei !== 'string') {
    return false;
  }

  // IMEI should be exactly 15 digits
  const imeiClean = imei.replace(/\D/g, ''); // Remove non-digits
  return imeiClean.length === 15;
};

/**
 * Set IMEI API key at runtime (for debugging when env vars don't work)
 * @param {string} apiKey - The API key to use
 */
export const setIMEIAPIKeyRuntime = (apiKey) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('IMEI_API_KEY_DEBUG', apiKey);
    console.log('🔧 Runtime API key set, please refresh the page');
  }
};

/**
 * Debug function to test IMEI API directly
 * @param {string} testImei - Test IMEI number (default: 123456789012345)
 * @returns {Promise<void>}
 */
export const debugIMEIAPI = async (testImei = '123456789012345') => {
  console.log('🧪 Debug: Testing IMEI API with:', testImei);
  
  // Refresh config to get latest values
  const currentConfig = getIMEIAPIConfig();
  console.log('🧪 Current runtime config:', {
    apiKeySet: !!currentConfig.apiKey,
    apiKeyLength: currentConfig.apiKey ? currentConfig.apiKey.length : 0
  });
  
  const result = await lookupDeviceByIMEI(testImei);
  console.log('🧪 Debug: IMEI API result:', result);
  return result;
};

export default {
  lookupDeviceByIMEI,
  processIMEIScreenshot,
  validateIMEI,
  debugIMEIAPI,
  setIMEIAPIKeyRuntime,
};
